import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@goaltree/ui/components/field'
import { Input } from '@goaltree/ui/components/input'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import { authClient } from '@/lib/auth-client'

export const Route = createFileRoute('/auth/check-email')({
  head: () => ({
    meta: [{ title: 'Check your email — Goaltree' }],
  }),
  validateSearch: (search): { email?: string } => ({
    email: typeof search.email === 'string' ? search.email : undefined,
  }),
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { email } = Route.useSearch()
  const [resending, setResending] = useState(false)
  const [resent, setResent] = useState(false)
  const [editableEmail, setEditableEmail] = useState(email ?? '')
  const [emailError, setEmailError] = useState('')

  // Auto-advance once the email is verified. On desktop the verification link
  // opens in the browser (it starts on the Convex auth domain, out of the PWA's
  // scope, then redirects) — but an installed PWA shares Chrome's cookie jar, so
  // that verification sets a session valid for this window too. Poll for it
  // (better-auth won't refetch on its own — refetchOnWindowFocus is off) and
  // sign the PWA in the moment it's verified, so the user never has to return to
  // the app manually. Re-checks immediately whenever the window regains focus.
  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (cancelled || document.visibilityState === 'hidden') return
      try {
        const res = await authClient.getSession({ query: { disableCookieCache: true } })
        if (!cancelled && res?.data) navigate({ to: '/today', replace: true })
      } catch {
        /* keep polling */
      }
    }
    void check()
    const id = setInterval(check, 3000)
    const onFocus = () => void check()
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      cancelled = true
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [navigate])

  async function handleResend() {
    const parsed = z.email().safeParse(editableEmail)
    if (!parsed.success) {
      setEmailError(t('auth.invalidEmail'))
      return
    }
    setEmailError('')
    setResending(true)
    await authClient.sendVerificationEmail(
      { email: editableEmail },
      {
        onSuccess: () => {
          setResent(true)
          toast.success(t('auth.verificationSent'))
        },
        onError: (error) => void toast.error(error.error.message || error.error.statusText),
      },
    )
    setResending(false)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-1 text-center">
        <h1 className="text-xl font-bold">{t('auth.checkEmailTitle')}</h1>
        <p className="text-sm text-muted-foreground">
          {email ? (
            <>
              {t('auth.checkEmailSentPre')}{' '}
              <span className="font-medium text-foreground">{email}</span>
            </>
          ) : (
            t('auth.checkEmailGeneric')
          )}
        </p>
      </div>

      <FieldGroup>
        <Field data-invalid={emailError ? true : undefined}>
          <FieldLabel>{t('auth.emailLabel')}</FieldLabel>
          <Input
            type="email"
            value={editableEmail}
            onChange={(e) => {
              setEditableEmail(e.target.value)
              setEmailError('')
              setResent(false)
            }}
            placeholder="you@example.com"
          />
          {emailError && <FieldError errors={[{ message: emailError }]} />}
        </Field>

        <Field>
          <button
            type="button"
            onClick={handleResend}
            disabled={resending || resent}
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.04] py-2.5 text-sm font-medium text-foreground/70 transition-all hover:border-foreground/20 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('auth.sending')}
              </>
            ) : resent ? (
              t('auth.emailSent')
            ) : (
              t('auth.resendVerification')
            )}
          </button>
          <FieldDescription className="text-center">
            {t('auth.alreadyVerified')}{' '}
            <button
              type="button"
              onClick={() => navigate({ to: '/auth/login' })}
              className="cursor-pointer font-medium text-foreground underline underline-offset-4 hover:text-primary-text"
            >
              {t('auth.signIn')}
            </button>
          </FieldDescription>
        </Field>
      </FieldGroup>
    </div>
  )
}
